package com.htmlagent.site.components.sections.landing

import androidx.compose.runtime.Composable
import com.htmlagent.site.components.sections.Glyph
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.theme.CtaNoteStyle
import com.htmlagent.site.theme.Icons
import com.htmlagent.site.theme.SampleActionStyle
import com.htmlagent.site.theme.SampleCardStyle
import com.htmlagent.site.theme.SampleFootStyle
import com.htmlagent.site.theme.SampleGridStyle
import com.htmlagent.site.theme.SampleKindStyle
import com.htmlagent.site.theme.SampleLineStyle
import com.htmlagent.site.theme.SampleNameStyle
import com.htmlagent.site.theme.SamplePillStyle
import com.htmlagent.site.theme.SampleShotStyle
import com.htmlagent.site.theme.Tokens
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.attributes.ATarget
import org.jetbrains.compose.web.attributes.target
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * One page the agent has written, as a thumbnail that opens the real thing.
 *
 * [artifact] is the public identifier of a seeded example, not a session: those pages live in
 * the database from a migration and are protected against deletion, so the link cannot rot.
 */
private data class Sample(
    val artifact: String,
    val shot: String,
    val name: String,
    val kind: String,
    val line: String,
)

/**
 * The pages the agent has actually written, each one opening on the artifact origin.
 *
 * They are screenshots and not live frames on purpose: six iframes would be several megabytes
 * and six connections on a page nobody has decided to trust yet. The screenshot argues, the
 * link proves.
 *
 * It replaced a strip of names sliding past, which was cheaper and claimed the same thing
 * without showing it — and five of the eight briefs it listed had no page behind them.
 */
@Composable
fun LandingSamples() {
    val t = LocalStrings.current
    val samples = listOf(
        Sample(
            artifact = "dec84ef3-b5e1-4289-930f-7c814c0e5d0f",
            shot = "the-boys",
            name = t.landing.sampleBoysName,
            kind = t.landing.sampleBoysKind,
            line = t.landing.sampleBoysLine,
        ),
        Sample(
            artifact = "8070adb7-12e7-48fb-a2ac-d6192eed9bb6",
            shot = "casa-liria",
            name = t.landing.sampleLiriaName,
            kind = t.landing.sampleLiriaKind,
            line = t.landing.sampleLiriaLine,
        ),
        Sample(
            artifact = "ccbe7479-9345-41f1-ab4c-780ba8a98be7",
            shot = "huerta-del-tormes",
            name = t.landing.sampleTormesName,
            kind = t.landing.sampleTormesKind,
            line = t.landing.sampleTormesLine,
        ),
        Sample(
            artifact = "1385f5ba-6c3d-4884-990b-8e0d32e22a7d",
            shot = "umbra",
            name = t.landing.sampleUmbraName,
            kind = t.landing.sampleUmbraKind,
            line = t.landing.sampleUmbraLine,
        ),
        Sample(
            artifact = "82cd2259-4a59-4a0b-b8e2-0c9087be11a9",
            shot = "nudo-norte",
            name = t.landing.sampleNudoName,
            kind = t.landing.sampleNudoKind,
            line = t.landing.sampleNudoLine,
        ),
        Sample(
            artifact = "b1b9156b-bb06-4f9e-8758-1bbd6de61ee6",
            shot = "ferro-norte",
            name = t.landing.sampleFerroName,
            kind = t.landing.sampleFerroKind,
            line = t.landing.sampleFerroLine,
        ),
    )

    Band(SECTION_SAMPLES, ink = true) {
        SectionHead(
            t.landing.samplesEyebrow,
            t.landing.samplesTitle,
            ink = true,
            headingId = headingId(SECTION_SAMPLES),
        )

        Div(SampleGridStyle.toModifier().toAttrs()) {
            samples.forEachIndexed { position, sample -> SampleCard(sample, position) }
        }

        P(CtaNoteStyle.toModifier().toAttrs { attr("data-align", "center") }) {
            Text(t.landing.samplesNote)
        }
    }
}

/**
 * A thumbnail that opens the page it shows.
 *
 * `rel="noopener"` because the destination runs untrusted code: without it the opened page gets
 * a handle on this one through `window.opener`. The ratio is declared on the image so the grid
 * does not jump while the six load, and only the first two load eagerly — the band sits halfway
 * down the page and the rest can wait until somebody scrolls to it.
 */
@Composable
private fun SampleCard(sample: Sample, position: Int) {
    val t = LocalStrings.current

    // The label names the page and the description carries the line: an `aria-label` replaces the
    // content it labels, so without `aria-describedby` the line below would be announced to
    // nobody and the six links would differ only by name.
    val lineId = "sample-${sample.shot}-line"

    A(
        href = "$ARTIFACT_ORIGIN/a/${sample.artifact}/1",
        attrs = SampleCardStyle.toModifier().toAttrs {
            target(ATarget.Blank)
            attr("rel", "noopener")
            attr("aria-label", t.landing.samplesOpen(sample.name))
            attr("aria-describedby", lineId)
        },
    ) {
        Img(
            src = "/samples/${sample.shot}.webp",
            attrs = SampleShotStyle.toModifier().toAttrs {
                // No width or height: the card is a fixed height and the shot fills it from the
                // corners, so its intrinsic size cannot move anything while it loads.
                //
                // The alt is not empty even though the card names the page right below it: the
                // link carries an `aria-label`, which replaces everything inside it, so a screen
                // reader never reaches this text and hears nothing twice. What it does reach is
                // image search, which had six screenshots of generated pages described as nothing.
                attr("alt", t.landing.samplesShotAlt(sample.name))
                attr("loading", if (position < EAGER_SHOTS) "eager" else "lazy")
                attr("decoding", "async")
            },
        )

        Div(SampleFootStyle.toModifier().toAttrs()) {
            Div(SampleKindStyle.toModifier().toAttrs()) { Text(sample.kind) }
            Div(SampleNameStyle.toModifier().toAttrs()) { Text(sample.name) }
            P(SampleLineStyle.toModifier().toAttrs { attr("id", lineId) }) { Text(sample.line) }

            Div(SampleActionStyle.toModifier().toAttrs()) {
                // A span and not a button: the card is already the link, and a control inside a
                // link is a control nobody can reach with the keyboard.
                Span(SamplePillStyle.toModifier().toAttrs()) {
                    Text(t.landing.samplesAction)
                    Glyph(Icons.ArrowUpRight, Tokens.iconSm.value())
                }
            }
        }
    }
}

/** How many load without waiting: the two that can be on screen when the band comes into view. */
private const val EAGER_SHOTS = 2

/**
 * Where the examples are served from. The artifact origin is separate on purpose, in production
 * too: the generated HTML is untrusted code and a path on the same origin would isolate nothing.
 *
 * It is a constant and no longer derived from `window.location`: these six links are the only
 * absolute URLs the landing puts in its markup, and `kobweb export` prerenders on localhost, so
 * a host-derived origin shipped `http://localhost:8082` inside the production HTML. The seeded
 * examples come from a migration and carry the same identifiers in every environment, so a
 * fixed origin resolves everywhere the artifact server is deployed.
 */
private const val ARTIFACT_ORIGIN = "https://artifacts.halagent.dev"
